<template>
  <v-main>
    <loader :loading="loading"></loader>
    <v-container fluid>
      <div class="sl-breadcrumbs-container">
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </div>

      <!-- Hero Guide Card -->
      <v-card class="sl-hero-card mb-6" elevation="4">
        <div class="sl-hero-header">
          <div class="sl-hero-header-content">
            <div class="d-flex align-center">
              <v-avatar size="52" class="mr-4 sl-hero-logo">
                <v-icon size="30" color="white">mdi-certificate-outline</v-icon>
              </v-avatar>
              <div>
                <h1 class="sl-hero-title">Subject of Certification</h1>
                <p class="sl-hero-subtitle">
                  Define, manage &amp; track your certification targets
                </p>
              </div>
            </div>
          </div>
          <v-btn class="sl-new-btn" @click="$router.push({ name: 'SubjectOfCertification' })">
            <v-icon left size="20">mdi-plus-circle</v-icon>
            {{ $t('soc.newbutton') }}
          </v-btn>
        </div>
        <v-card-text class="pt-5 pb-6 px-6">
          <p class="sl-intro-text mb-5">
            A Subject of Certification (SoC) is the specific system, product, or process that will
            undergo certification against security standards like IEC 62443 or Cyber Resilience Act.
            Creating a Subject of Certification is the first step in the compliance assessment
            process.
          </p>
          <v-row>
            <v-col cols="12" md="6">
              <div class="sl-feature-box">
                <div class="sl-feature-header">
                  <div class="sl-feature-icon-box sl-icon-info">
                    <v-icon color="white" size="24">mdi-information-outline</v-icon>
                  </div>
                  <span class="sl-feature-title">What is a Subject of Certification?</span>
                </div>
                <ul class="sl-feature-list">
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Defines the
                    scope and boundaries of what needs to be certified
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Specifies
                    which security standard family applies (IEC 62443, CRA, etc.)
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Documents
                    the technical components or organizational processes
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Establishes
                    which requirements must be satisfied
                  </li>
                  <li>
                    <v-icon small color="#0097a7" class="mr-2">mdi-check-circle</v-icon>Forms the
                    foundation for building a Security Assurance Case
                  </li>
                </ul>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="sl-feature-box">
                <div class="sl-feature-header">
                  <div class="sl-feature-icon-box sl-icon-success">
                    <v-icon color="white" size="24">mdi-checkbox-marked-circle-outline</v-icon>
                  </div>
                  <span class="sl-feature-title">Creating a Subject of Certification</span>
                </div>
                <ol class="sl-workflow-list">
                  <li>
                    <span class="sl-step-num">1</span>Click the "New Subject of Certification"
                    button to start the creation process
                  </li>
                  <li>
                    <span class="sl-step-num">2</span>Provide a name and detailed description for
                    your system or product
                  </li>
                  <li>
                    <span class="sl-step-num">3</span>Select the appropriate certification baseline
                    (IEC 62443, CRA, etc.)
                  </li>
                  <li>
                    <span class="sl-step-num">4</span>Based on your selection, provide specific
                    information about your system
                  </li>
                  <li>
                    <span class="sl-step-num">5</span>Review and select which requirements apply to
                    your system
                  </li>
                  <li>
                    <span class="sl-step-num">6</span>Finalize your Subject of Certification to
                    begin the assessment
                  </li>
                </ol>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Data Table Card -->
      <v-card class="sl-table-card mb-6" elevation="3">
        <div class="sl-table-header">
          <v-icon color="white" size="24" class="mr-3">mdi-table-large</v-icon>
          <span class="sl-table-header-text">{{ $tc('soc.title') }}</span>
          <v-spacer></v-spacer>
          <v-chip dark small class="sl-count-chip">
            <v-icon left x-small>mdi-counter</v-icon>
            {{ soc.length }} Certifications
          </v-chip>
        </div>
        <v-card-text class="pa-5">
          <div class="d-flex align-center mb-4" style="gap: 12px;">
            <v-select
              v-model="searchField"
              :items="searchFields"
              item-text="text"
              item-value="value"
              :label="$t('search-field')"
              hide-details
              outlined
              dense
              class="sl-search-field"
              style="max-width: 200px;"
            ></v-select>
            <v-text-field
              v-model="searchQuery"
              append-icon="mdi-magnify"
              :label="$t('search')"
              hide-details
              class="flex-grow-1"
              outlined
              dense
              @keyup.enter="applySearch"
            />
          </div>
          <v-data-table
            :headers="getHeaders"
            :items="soc"
            :search="searchQuery"
            :custom-filter="customFilter"
            class="sl-data-table elevation-0"
            :loading="loading"
            :items-per-page="5"
            :footer-props="{
              showFirstLastPage: true,
              firstIcon: 'mdi-arrow-collapse-left',
              lastIcon: 'mdi-arrow-collapse-right',
              prevIcon: 'mdi-arrow-left',
              nextIcon: 'mdi-arrow-right',
              disableItemsPerPage: false,
              itemsPerPageText: getItemsPerPageText,
              showCurrentPage: true,
            }"
            must-sort
            :sort-desc="true"
          >
            <template v-slot:[`item.creationDate`]="{ item }">
              <span>{{ formatDate(item.creationDate) }}</span>
            </template>
            <template v-slot:[`item.origin`]="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">
                    {{ item.origin === 'spider' ? 'mdi-spider' : 'mdi-account-box' }}
                  </v-icon>
                </template>
                <span>{{
                  item.origin === 'spider' ? 'Created by Threat Spider' : 'Created Manually'
                }}</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                x-small
                class="sl-action-btn sl-action-view mr-1"
                @click="enablePreviewSocDialog(item.id)"
              >
                <v-icon size="18">mdi-eye</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                class="sl-action-btn sl-action-edit mr-1"
                @click="enableEditSocDialog(item)"
              >
                <v-icon size="18">mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                class="sl-action-btn sl-action-delete"
                @click="confirmDeleteSoc(item.id)"
              >
                <v-icon size="18">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Preview Dialog -->
    <v-dialog v-model="previewSocClicked" max-width="900" scrollable>
      <v-card v-if="socInfo" class="sl-dialog-card">
        <div class="sl-dialog-header">
          <v-icon color="white" size="24" class="mr-3">mdi-eye</v-icon>
          <span class="sl-dialog-title">{{ socInfo.name }}</span>
          <span class="sl-dialog-subtitle ml-2">(ID: {{ socInfo.id }})</span>
          <v-spacer></v-spacer>
          <v-btn icon dark small @click="previewSocClicked = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-4" style="max-height: 600px;">
          <v-container>
            <v-row>
              <v-col cols="12" class="sl-preview-item">
                <div class="sl-preview-icon-box">
                  <v-icon color="white" size="18">mdi-format-list-text</v-icon>
                </div>
                <strong class="sl-preview-label">{{ $t('soc.headers.type') }}: </strong>
                <span class="sl-preview-value">{{ socInfo.type }}</span>
              </v-col>
              <v-col cols="12" class="sl-preview-item">
                <div class="sl-preview-icon-box">
                  <v-icon color="white" size="18">mdi-text-box-outline</v-icon>
                </div>
                <strong class="sl-preview-label">{{ $t('soc.headers.description') }}: </strong>
                <span class="sl-preview-value text-wrap">{{ socInfo.description }}</span>
              </v-col>
              <v-col cols="12" class="sl-preview-item">
                <div class="sl-preview-icon-box">
                  <v-icon color="white" size="18">mdi-book-open-variant</v-icon>
                </div>
                <strong class="sl-preview-label">{{ $t('soc.headers.scope') }}: </strong>
                <span class="sl-preview-value">{{ socInfo.standardFamily }}</span>
              </v-col>
              <v-col cols="12" class="sl-preview-item">
                <div class="sl-preview-icon-box">
                  <v-icon color="white" size="18">mdi-calendar</v-icon>
                </div>
                <strong class="sl-preview-label">{{ $t('soc.headers.date') }}: </strong>
                <span class="sl-preview-value">{{ formatDate(socInfo.creationDate) }}</span>
              </v-col>
              <v-col cols="12" class="sl-preview-item">
                <div class="sl-preview-icon-box">
                  <v-icon color="white" size="18">mdi-tag-outline</v-icon>
                </div>
                <strong class="sl-preview-label">{{ $t('soc.headers.version') }}: </strong>
                <span class="sl-preview-value">{{ socInfo.versionLabel }}</span>
              </v-col>

              <!-- Requirements Section -->
              <v-col cols="12" class="mt-4">
                <v-divider class="mb-4"></v-divider>
                <div class="d-flex align-center mb-3">
                  <v-icon color="primary" class="mr-2">mdi-clipboard-list-outline</v-icon>
                  <span class="text-h6 font-weight-bold">Requirements</span>
                </div>

                <v-tabs v-model="requirementsTab" background-color="transparent" color="primary">
                  <v-tab>
                    <v-icon left small>mdi-check-circle</v-icon>
                    In Scope ({{ inScopeRequirements.length }})
                  </v-tab>
                  <v-tab>
                    <v-icon left small>mdi-close-circle</v-icon>
                    Out of Scope ({{ outOfScopeRequirements.length }})
                  </v-tab>
                </v-tabs>

                <v-tabs-items v-model="requirementsTab" class="mt-3">
                  <v-tab-item>
                    <v-card flat v-if="inScopeRequirements.length > 0">
                      <v-list dense>
                        <v-list-item
                          v-for="req in inScopeRequirements"
                          :key="req.id"
                          class="sl-requirement-item mb-2"
                        >
                          <v-list-item-avatar>
                            <v-icon color="success">mdi-check-circle</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold"
                              >{{ req.id }} - {{ req.title }}</v-list-item-title
                            >
                            <v-list-item-subtitle class="text-wrap">{{
                              req.description
                            }}</v-list-item-subtitle>
                            <div class="mt-1">
                              <v-chip x-small color="primary" outlined>{{ req.family }}</v-chip>
                              <v-chip x-small class="ml-1" color="secondary" outlined>
                                Requirement ID: {{ req.requirementId }}
                              </v-chip>
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card>
                    <v-alert v-else type="info" text class="mt-2"
                      >No in-scope requirements found</v-alert
                    >
                  </v-tab-item>
                  <v-tab-item>
                    <v-card flat v-if="outOfScopeRequirements.length > 0">
                      <v-list dense>
                        <v-list-item
                          v-for="req in outOfScopeRequirements"
                          :key="req.id"
                          class="sl-requirement-item mb-2"
                        >
                          <v-list-item-avatar>
                            <v-icon color="error">mdi-close-circle</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title class="font-weight-bold"
                              >{{ req.id }} - {{ req.title }}</v-list-item-title
                            >
                            <v-list-item-subtitle class="text-wrap">{{
                              req.description
                            }}</v-list-item-subtitle>
                            <div class="mt-1">
                              <v-chip x-small color="primary" outlined>{{ req.family }}</v-chip>
                              <v-chip x-small class="ml-1" color="secondary" outlined>
                                Requirement ID: {{ req.requirementId }}
                              </v-chip>
                            </div>
                            <div v-if="req.justification" class="mt-2">
                              <strong class="text-caption">Justification:</strong>
                              <div class="text-caption text-wrap grey--text text--darken-2">
                                {{ req.justification }}
                              </div>
                            </div>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                    </v-card>
                    <v-alert v-else type="info" text class="mt-2"
                      >No out-of-scope requirements found</v-alert
                    >
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editSocClicked" max-width="960" scrollable>
      <v-card v-if="socEditInfo" class="sl-dialog-card">
        <!-- Header -->
        <div class="sl-dialog-header">
          <v-icon color="white" size="24" class="mr-3">mdi-pencil</v-icon>
          <div class="d-flex flex-column">
            <span class="sl-dialog-title">{{ socEditInfo.name }}</span>
            <span class="sl-dialog-subtitle"
              >ID: {{ socEditInfo.id }} · {{ socEditInfo.standardFamily }}</span
            >
          </div>
          <v-spacer></v-spacer>
          <v-chip
            v-if="pendingChangesCount > 0"
            small
            color="amber darken-1"
            text-color="white"
            class="mr-2 font-weight-bold"
          >
            <v-icon left x-small>mdi-circle-edit-outline</v-icon>
            {{ pendingChangesCount }} pending
          </v-chip>
          <v-btn icon dark small @click="editSocClicked = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Tabs -->
        <v-tabs
          v-model="editTab"
          background-color="#f8f9fa"
          color="#006494"
          slider-color="#006494"
          dense
        >
          <v-tab class="sl-edit-tab">
            <v-icon left small>mdi-information-outline</v-icon>
            Basic Info
          </v-tab>
          <v-tab class="sl-edit-tab">
            <v-icon left small>mdi-format-list-checks</v-icon>
            Requirements
            <v-chip
              x-small
              class="ml-2"
              :color="pendingChangesCount > 0 ? 'amber darken-1' : '#e0e0e0'"
              :text-color="pendingChangesCount > 0 ? 'white' : '#757575'"
            >
              {{ editRequirements.length }}
            </v-chip>
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>

        <v-card-text class="pa-0" style="max-height: 520px; overflow-y: auto;">
          <v-tabs-items v-model="editTab">
            <!-- ── Tab 1: Basic Info ── -->
            <v-tab-item>
              <div class="pa-6">
                <v-text-field
                  v-model="socEditInfo.name"
                  :label="$t('soc.headers.name')"
                  :rules="[v => !!v || 'Name is required']"
                  required
                  outlined
                  dense
                  class="mb-4"
                  prepend-inner-icon="mdi-format-title"
                  placeholder="Enter a name for the Subject of Certification"
                  hide-details="auto"
                />
                <v-textarea
                  v-model="socEditInfo.description"
                  :label="$t('soc.headers.description')"
                  :rules="[
                    v => !!v || 'Description is required',
                    v => v.length <= 500 || 'Description must be less than 500 characters',
                  ]"
                  required
                  outlined
                  auto-grow
                  rows="4"
                  row-height="25"
                  class="mb-4"
                  counter="500"
                  prepend-inner-icon="mdi-text-box-outline"
                  placeholder="Enter detailed description"
                  hide-details="auto"
                />
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="socEditInfo.standardFamily"
                      :label="$t('soc.headers.scope')"
                      disabled
                      outlined
                      dense
                      class="sl-disabled-field"
                      prepend-inner-icon="mdi-book-open-variant"
                      hide-details="auto"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="socEditInfo.versionLabel"
                      :label="$t('soc.headers.version')"
                      disabled
                      outlined
                      dense
                      class="sl-disabled-field"
                      prepend-inner-icon="mdi-tag-outline"
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>

            <!-- ── Tab 2: Requirements ── -->
            <v-tab-item>
              <div class="pa-4">
                <!-- Stats bar -->
                <div class="sl-req-stats-bar mb-3">
                  <div class="sl-req-stat sl-req-stat--in">
                    <v-icon size="16" color="success" class="mr-1">mdi-check-circle</v-icon>
                    <span>{{ editReqStats.inScope }} in scope</span>
                  </div>
                  <div class="sl-req-stat sl-req-stat--out">
                    <v-icon size="16" color="error" class="mr-1">mdi-minus-circle</v-icon>
                    <span>{{ editReqStats.outOfScope }} out of scope</span>
                  </div>
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="reqSearchQuery"
                    dense
                    outlined
                    hide-details
                    placeholder="Filter requirements…"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    class="sl-req-search"
                  />
                </div>

                <!-- Loading -->
                <div v-if="editRequirementsLoading" class="text-center py-8">
                  <v-progress-circular
                    indeterminate
                    color="#006494"
                    size="40"
                  ></v-progress-circular>
                  <p class="mt-3 grey--text text--darken-1">Loading requirements…</p>
                </div>

                <!-- Empty -->
                <div v-else-if="!editRequirements.length" class="text-center py-8">
                  <v-icon size="48" color="grey lighten-2">mdi-clipboard-text-off-outline</v-icon>
                  <p class="mt-2 grey--text">No requirements found for this SoC.</p>
                </div>

                <!-- Requirements list -->
                <div v-else>
                  <div
                    v-for="req in filteredEditRequirements"
                    :key="req.id"
                    class="sl-req-row"
                    :class="{
                      'sl-req-row--in': req.inScope,
                      'sl-req-row--out': !req.inScope,
                      'sl-req-row--changed': !!reqChanges[String(req.id)],
                    }"
                  >
                    <div class="sl-req-row-main">
                      <!-- Toggle -->
                      <v-tooltip top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-switch
                            :input-value="req.inScope"
                            @change="onToggleScope(req, $event)"
                            :color="req.inScope ? 'success' : 'error'"
                            dense
                            hide-details
                            class="sl-req-toggle mt-0 pt-0"
                            v-bind="attrs"
                            v-on="on"
                          />
                        </template>
                        <span>{{
                          req.inScope ? 'Click to set Out of Scope' : 'Click to set In Scope'
                        }}</span>
                      </v-tooltip>

                      <!-- Info -->
                      <div class="sl-req-info">
                        <div class="sl-req-title">
                          {{ req.title || req.description || '(No title)' }}
                        </div>
                        <div class="sl-req-meta">
                          <v-chip
                            x-small
                            label
                            :color="req.inScope ? '#e8f5e9' : '#fce4ec'"
                            :text-color="req.inScope ? '#2e7d32' : '#c62828'"
                            class="mr-1"
                          >
                            {{ req.inScope ? 'In Scope' : 'Out of Scope' }}
                          </v-chip>
                          <v-chip
                            v-if="req.requirementId"
                            x-small
                            label
                            color="#ede7f6"
                            text-color="#4527a0"
                            class="mr-1"
                            >Requirement ID: {{ req.requirementId }}</v-chip
                          >
                          <v-chip
                            v-if="req.family"
                            x-small
                            label
                            color="#e8eaf6"
                            text-color="#3949ab"
                            class="mr-1"
                            >{{ req.family }}</v-chip
                          >
                          <v-chip
                            v-if="req.status"
                            x-small
                            label
                            color="#f3e5f5"
                            text-color="#7b1fa2"
                            >{{ req.status }}</v-chip
                          >
                          <span v-if="reqChanges[String(req.id)]" class="sl-req-changed-badge">
                            <v-icon x-small color="amber darken-2" class="mr-1"
                              >mdi-circle-edit-outline</v-icon
                            >modified
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Justification (shown when out of scope) -->
                    <v-expand-transition>
                      <div v-if="!req.inScope" class="sl-req-justification">
                        <v-textarea
                          :value="req.justification"
                          @input="onJustificationChange(req, $event)"
                          label="Justification for exclusion"
                          outlined
                          dense
                          rows="2"
                          auto-grow
                          hide-details
                          placeholder="Explain why this requirement is out of scope…"
                          prepend-inner-icon="mdi-comment-text-outline"
                          class="sl-justification-field"
                        />
                      </div>
                    </v-expand-transition>
                  </div>

                  <div
                    v-if="filteredEditRequirements.length === 0"
                    class="text-center py-4 grey--text"
                  >
                    No requirements match your filter.
                  </div>
                </div>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <div v-if="pendingChangesCount > 0" class="sl-pending-hint">
            <v-icon small color="amber darken-2" class="mr-1">mdi-information-outline</v-icon>
            <span>{{ pendingChangesCount }} requirement change(s) will be saved</span>
          </div>
          <v-spacer></v-spacer>
          <v-btn text class="sl-dialog-close" @click="editSocClicked = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn class="sl-dialog-save-btn" @click="saveSocEdits" :loading="loading">
            <v-icon left size="18">mdi-content-save</v-icon>
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="sl-dialog-card">
        <div class="sl-dialog-header-danger">
          <v-icon color="white" size="24" class="mr-3">mdi-alert-circle</v-icon>
          <span class="sl-dialog-title">{{ $t('delete.confirm') }}</span>
        </div>
        <v-card-text class="pa-6 text-center">
          <v-icon size="64" color="#ef5350" class="mb-3">mdi-delete-alert</v-icon>
          <p class="text-body-1 grey--text text--darken-2">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0 justify-center">
          <v-btn text class="sl-dialog-close mr-3" @click="deleteDialog = false">{{
            $t('cancel')
          }}</v-btn>
          <v-btn class="sl-dialog-delete-btn" @click="deleteSoc">
            <v-icon left size="18">mdi-delete</v-icon>
            {{ $t('delete.message') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import Soc from '@/modules/soc/api/soc.js';
import inScopeRequirementsApi from '@/modules/sac/api/inScopeRequirementsApi.js';
import Loader from '@/components/helpers/Loader.vue';
// import Header from '@/components/helpers/Header';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'SoCList',
  mixins: [util],
  components: {
    Loader,
    // Header,
    Breadcrumbs,
  },
  computed: {
    breadcrumbs() {
      return this.$route.meta?.breadcrumbs || [];
    },
    getHeaders() {
      return [
        {
          text: this.$t('soc.headers.id'),
          value: 'id',
          class: 'text text-subtitle-2 font-weight-black',
          width: '5%',
        },
        {
          text: this.$t('soc.headers.name'),
          value: 'name',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('soc.headers.type'),
          value: 'type',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('soc.headers.description'),
          value: 'description',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '15%',
        },
        {
          text: this.$t('soc.headers.scope'),
          value: 'standardFamily',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        { text: this.$t('soc.headers.date'), value: 'creationDate', align: 'center', width: '10%' },
        {
          text: this.$t('soc.headers.version'),
          value: 'versionLabel',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('Origin'),
          value: 'origin',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
        {
          text: this.$t('soc.headers.actions'),
          value: 'actions',
          align: 'center',
          class: 'text text-subtitle-2 font-weight-black',
          width: '10%',
        },
      ];
    },
    getItemsPerPageText() {
      return this.$t('soc.table-footer');
    },
    searchFields() {
      return [
        { text: this.$t('search-field-all'), value: 'all' },
        { text: this.$t('soc.headers.id'), value: 'id' },
        { text: this.$t('soc.headers.name'), value: 'name' },
        { text: this.$t('soc.headers.type'), value: 'type' },
        { text: this.$t('soc.headers.description'), value: 'description' },
        { text: this.$t('soc.headers.scope'), value: 'standardFamily' },
        { text: this.$t('soc.headers.version'), value: 'versionLabel' },
      ];
    },
    inScopeRequirements() {
      return this.socRequirements.filter(req => req.inScope === true);
    },
    outOfScopeRequirements() {
      return this.socRequirements.filter(req => req.inScope === false);
    },
    filteredEditRequirements() {
      if (!this.editRequirements.length) return [];
      const q = (this.reqSearchQuery || '').toLowerCase().trim();
      if (!q) return this.editRequirements;
      return this.editRequirements.filter(
        r =>
          (r.title || '').toLowerCase().includes(q) ||
          (r.family || '').toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q),
      );
    },
    pendingChangesCount() {
      return Object.keys(this.reqChanges).length;
    },
    editReqStats() {
      const inScope = this.editRequirements.filter(r => r.inScope).length;
      return { inScope, outOfScope: this.editRequirements.length - inScope };
    },
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      searchField: 'all',
      soc: [],
      previewSocClicked: false,
      socInfo: null,
      deleteDialog: false,
      editSocClicked: false,
      deleteSocId: null,
      socEditInfo: null,
      requirementsTab: 0,
      socRequirements: [],
      // edit dialog extras
      editTab: 0,
      editRequirements: [],
      editRequirementsLoading: false,
      reqSearchQuery: '',
      reqChanges: {},
    };
  },
  created() {
    this.fetchSocs();
  },
  methods: {
    async fetchSocs() {
      this.loading = true;
      try {
        const response = await Soc.all();
        if (response && response.data) {
          this.soc = response.data;
        } else {
          console.error('Invalid response from Soc.all()');
        }
      } catch (error) {
        console.error('Error fetching socs:', error);
      } finally {
        this.loading = false;
      }
    },
    async enablePreviewSocDialog(id) {
      this.loading = true;
      try {
        const { data } = await Soc.getById(id);
        this.socInfo = data;

        // Fetch requirements for this SoC
        const requirementsResponse = await Soc.getRequirements(id);
        this.socRequirements = requirementsResponse.data || [];

        this.requirementsTab = 0;
        this.previewSocClicked = true;
      } catch (error) {
        console.error('Error fetching SoC details:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },
    enableEditSocDialog(item) {
      this.socEditInfo = { ...item };
      this.editTab = 0;
      this.editRequirements = [];
      this.reqChanges = {};
      this.reqSearchQuery = '';
      this.editSocClicked = true;
      this.loadEditRequirements(item.id);
    },
    async loadEditRequirements(socId) {
      this.editRequirementsLoading = true;
      try {
        const res = await Soc.getRequirements(socId);
        this.editRequirements = (res.data || []).map(r => ({ ...r }));
      } catch (e) {
        console.error('Error loading requirements for edit:', e);
      } finally {
        this.editRequirementsLoading = false;
      }
    },
    onToggleScope(req, newValue) {
      const idx = this.editRequirements.findIndex(r => r.id === req.id);
      if (idx !== -1) {
        const updated = { ...this.editRequirements[idx], inScope: newValue };
        this.$set(this.editRequirements, idx, updated);
        this.$set(this.reqChanges, String(req.id), updated);
      }
    },
    onJustificationChange(req, value) {
      const idx = this.editRequirements.findIndex(r => r.id === req.id);
      if (idx !== -1) {
        const updated = { ...this.editRequirements[idx], justification: value };
        this.$set(this.editRequirements, idx, updated);
        this.$set(this.reqChanges, String(req.id), updated);
      }
    },
    revertRequirementChange(req) {
      const original = this.editRequirements.find(r => r.id === req.id);
      if (!original) return;
      const idx = this.editRequirements.findIndex(r => r.id === req.id);
      const withoutChange = { ...original };
      delete withoutChange.__pendingInScope;
      // find original from server by id — use reqChanges to restore
      const change = this.reqChanges[String(req.id)];
      if (change) {
        const restored = { ...change, inScope: !change.inScope };
        this.$set(this.editRequirements, idx, restored);
        this.$delete(this.reqChanges, String(req.id));
      }
    },
    async saveSocEdits() {
      this.loading = true;
      try {
        const payload = {
          id: this.socEditInfo.id,
          name: this.socEditInfo.name,
          description: this.socEditInfo.description,
          standardFamily: this.socEditInfo.standardFamily,
          creationDate: this.socEditInfo.creationDate,
          currentVersionId: this.socEditInfo.currentVersionId,
          versionLabel: this.socEditInfo.versionLabel,
          type: this.socEditInfo.type,
        };

        await Soc.update(payload);

        // Save any requirement scope/justification changes
        const changes = Object.values(this.reqChanges);
        if (changes.length > 0) {
          await Promise.all(changes.map(r => inScopeRequirementsApi.update(r)));
        }

        await this.fetchSocs();

        const index = this.soc.findIndex(item => item.id === this.socEditInfo.id);
        if (index !== -1) {
          this.soc[index] = { ...this.soc[index], ...payload };
        }

        EventBus.$emit('snackbar', {
          text:
            changes.length > 0
              ? `SoC updated · ${changes.length} requirement(s) changed`
              : 'Subject of Certification updated successfully',
          color: 'green',
        });

        this.editSocClicked = false;
      } catch (error) {
        console.error('Error updating SoC:', error);
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.loading = false;
      }
    },
    confirmDeleteSoc(id) {
      this.deleteSocId = id;
      this.deleteDialog = true;
    },
    async deleteSoc() {
      this.loading = true;
      try {
        await Soc.deleteById(this.deleteSocId);
        this.soc = this.soc.filter(item => item.id !== this.deleteSocId);
        EventBus.$emit('snackbar', {
          text: this.$t('delete.success'),
          color: 'green',
        });
      } catch (error) {
        EventBus.$emit('snackbar', {
          text: this.$t('errorlist.something-went-wrong'),
          color: 'red',
        });
      } finally {
        this.deleteDialog = false;
        this.loading = false;
      }
    },

    formatDate(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    customFilter(value, search, item) {
      if (!search) return true;

      search = search.toString().toLowerCase();

      if (this.searchField === 'all') {
        // Search in all fields (default behavior)
        return Object.keys(item).some(key => {
          // Skip action column and non-string values
          if (key === 'actions' || key === 'raw' || item[key] === null || item[key] === undefined) {
            return false;
          }

          try {
            const itemValue = item[key].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        });
      } else {
        // Search only in the selected field
        if (item[this.searchField] !== null && item[this.searchField] !== undefined) {
          try {
            const itemValue = item[this.searchField].toString().toLowerCase();
            return itemValue.includes(search);
          } catch (e) {
            return false;
          }
        }
        return false;
      }
    },
    applySearch() {
      // This method can be used for any additional search logic if needed
      // For now it's a placeholder as the filtering happens automatically
    },
  },
};
</script>

<style scoped>
/* ============================================
   SOC LIST - Modern Design System
   ============================================ */

.sl-breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 28px;
}

.sl-breadcrumbs-container .v-breadcrumbs {
  background-color: #ffffff;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Hero Card */
.sl-hero-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
  background: #ffffff;
}

.sl-hero-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 24px 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.sl-hero-header-content {
  flex: 1;
}

.sl-hero-logo {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
}

.sl-hero-title {
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
}

.sl-hero-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;
}

.sl-new-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 0 20px !important;
  transition: all 0.3s ease !important;
  backdrop-filter: blur(4px);
}

.sl-new-btn:hover {
  background: rgba(255, 255, 255, 0.35) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
}

.sl-intro-text {
  color: #424242;
  line-height: 1.7;
  font-size: 1.02rem;
}

/* Feature Boxes */
.sl-feature-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 14px;
  padding: 22px;
  height: 100%;
  border: 1px solid #e8eef3;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.sl-feature-box:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 64, 94, 0.12);
  border-color: #0097a7;
}

.sl-feature-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.sl-feature-icon-box {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.sl-icon-info {
  background: linear-gradient(135deg, #1976d2, #42a5f6);
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
}

.sl-icon-success {
  background: linear-gradient(135deg, #388e3c, #66bb6a);
  box-shadow: 0 4px 12px rgba(56, 142, 60, 0.3);
}

.sl-feature-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a237e;
}

.sl-feature-list,
.sl-workflow-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sl-feature-list li,
.sl-workflow-list li {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  font-size: 0.92rem;
  color: #455a64;
  line-height: 1.5;
}

.sl-step-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00405e, #0097a7);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Table Card */
.sl-table-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: none !important;
}

.sl-table-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.sl-table-header-text {
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sl-count-chip {
  background: rgba(255, 255, 255, 0.2) !important;
  color: white !important;
  font-weight: 600;
}

.sl-search-field {
  min-width: 150px;
}

.sl-data-table {
  border-radius: 0 0 16px 16px;
}

.sl-data-table ::v-deep thead th {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%) !important;
  color: #1a237e !important;
  font-weight: 700 !important;
  font-size: 0.82rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #e0e0e0 !important;
}

.sl-data-table ::v-deep tbody tr {
  transition: all 0.2s ease;
}

.sl-data-table ::v-deep tbody tr:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%) !important;
}

.sl-data-table ::v-deep tbody td {
  font-size: 0.9rem;
  color: #37474f;
  padding: 12px 16px;
}

/* Action Buttons */
.sl-action-btn {
  transition: all 0.3s ease !important;
  width: 32px !important;
  height: 32px !important;
}

.sl-action-view {
  color: #1976d2 !important;
}
.sl-action-view:hover {
  background: rgba(25, 118, 210, 0.1) !important;
  transform: scale(1.15);
}
.sl-action-edit {
  color: #ff9800 !important;
}
.sl-action-edit:hover {
  background: rgba(255, 152, 0, 0.1) !important;
  transform: scale(1.15);
}
.sl-action-delete {
  color: #ef5350 !important;
}
.sl-action-delete:hover {
  background: rgba(239, 83, 80, 0.1) !important;
  transform: scale(1.15);
}

/* Dialog Styles */
.sl-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.sl-dialog-header {
  background: linear-gradient(135deg, #00405e 0%, #006494 50%, #0097a7 100%);
  padding: 18px 24px;
  display: flex;
  align-items: center;
}

.sl-dialog-header-danger {
  background: linear-gradient(135deg, #c62828 0%, #ef5350 100%);
  padding: 18px 24px;
  display: flex;
  align-items: center;
}

.sl-dialog-title {
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
}

.sl-dialog-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
}

/* Preview Items */
.sl-preview-item {
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e8eef3;
  display: flex;
  align-items: flex-start;
  transition: all 0.2s ease;
}

.sl-preview-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-color: #0097a7;
}

.sl-preview-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1976d2, #42a5f6);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.sl-preview-label {
  color: #37474f;
  min-width: 110px;
  margin-right: 8px;
  font-size: 0.9rem;
}

.sl-preview-value {
  color: #546e7a;
  flex: 1;
  font-size: 0.9rem;
}

.text-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}

/* Requirement Items */
.sl-requirement-item {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid #e8eef3;
  padding: 8px;
  transition: all 0.2s ease;
}

.sl-requirement-item:hover {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Form Fields */
.sl-disabled-field {
  opacity: 0.8;
  background-color: #f5f5f5;
}

.sl-disabled-field ::v-deep .v-input__slot {
  background-color: #f5f5f5 !important;
}

.sl-dialog-close {
  color: #78909c !important;
  text-transform: none !important;
  font-weight: 500 !important;
}

.sl-dialog-save-btn {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3) !important;
}

.sl-dialog-save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(25, 118, 210, 0.5) !important;
}

.sl-dialog-delete-btn {
  background: linear-gradient(135deg, #c62828 0%, #ef5350 100%) !important;
  color: white !important;
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 12px rgba(198, 40, 40, 0.3) !important;
}

.sl-dialog-delete-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(198, 40, 40, 0.5) !important;
}

.v-tabs-items {
  background-color: transparent;
}

/* ── Edit dialog tabs ── */
.sl-edit-tab {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.88rem !important;
  letter-spacing: 0 !important;
}

/* ── Requirements stats bar ── */
.sl-req-stats-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.sl-req-stat {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}
.sl-req-stat--in {
  background: #e8f5e9;
  color: #2e7d32;
}
.sl-req-stat--out {
  background: #fce4ec;
  color: #c62828;
}
.sl-req-search {
  max-width: 240px;
  flex-shrink: 0;
}

/* ── Requirement row ── */
.sl-req-row {
  border-radius: 10px;
  margin-bottom: 8px;
  border: 1.5px solid #e0e0e0;
  background: #ffffff;
  transition: box-shadow 0.15s, border-color 0.15s;
  overflow: hidden;
}
.sl-req-row:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.sl-req-row--in {
  border-left: 4px solid #4caf50 !important;
  background: #fafffe;
}
.sl-req-row--out {
  border-left: 4px solid #ef5350 !important;
  background: #fffafa;
}
.sl-req-row--changed {
  border-top-color: #ffc107 !important;
  border-right-color: #ffc107 !important;
  border-bottom-color: #ffc107 !important;
}

.sl-req-row-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 14px 10px 10px;
}
.sl-req-toggle {
  flex-shrink: 0;
  margin-top: 2px !important;
}
.sl-req-info {
  flex: 1;
  min-width: 0;
}
.sl-req-title {
  font-size: 0.88rem;
  font-weight: 600;
  color: #263238;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 5px;
}
.sl-req-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}
.sl-req-changed-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  color: #f9a825;
}

/* ── Justification field ── */
.sl-req-justification {
  padding: 0 14px 12px 52px;
}
.sl-justification-field .v-input__slot {
  background: #fffde7 !important;
}

/* ── Pending hint in actions bar ── */
.sl-pending-hint {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  font-weight: 500;
  color: #f9a825;
}

/* Responsive */
@media (max-width: 960px) {
  .sl-hero-header {
    padding: 20px;
  }
  .sl-hero-title {
    font-size: 1.4rem;
  }
}
</style>
